import React, { Dispatch, memo, SetStateAction, useEffect, useRef } from 'react';
import {
	FieldErrors,
	FieldValues,
	UseFormClearErrors,
	UseFormGetValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormTrigger,
	UseFormWatch,
} from 'react-hook-form';

import {
	Button,
	GridContainer,
	Input,
	Label,
	RadioButton,
	TextArea,
} from '@components/common';

import { getIsRequiredField } from '@utils/assetForm';

import { IAssetFormValues } from '@interfaces/components/form';

import { ECoordFormat } from '@enums/common';

import { regExpDecimalAndHyphen } from '@constants/regExp';
import {
	addressAreas,
	fieldsToTestAddress,
	tabsNames,
} from '../constants';

import styles from './Address.module.scss';

interface IAddressProps {
	clearErrors: UseFormClearErrors<FieldValues>;
	errors: FieldErrors;
	getValues: UseFormGetValues<FieldValues>;
	isEditMode: boolean;
	register: UseFormRegister<FieldValues>;
	saveValues: (values: IAssetFormValues) => void;
	setCurrentTab: Dispatch<SetStateAction<string>>;
	setValue: UseFormSetValue<FieldValues>;
	trigger: UseFormTrigger<FieldValues>;
	watch: UseFormWatch<FieldValues>;
	requiredFields?: string[] | null;
}

const Address = ({
	clearErrors,
	errors,
	getValues,
	isEditMode,
	register,
	saveValues,
	setCurrentTab,
	setValue,
	trigger,
	watch,
	requiredFields,
}: IAddressProps) => {
	const addressRef: React.Ref<any> = useRef(null);
	const onSubmit = async () => {
		const isValid = await trigger(fieldsToTestAddress);

		if (!isValid) return;

		if (!isEditMode) {
			saveValues(getValues());
			setCurrentTab(tabsNames.attributes);
		}
	};

	const coordFormat = watch('coordFormat');
	const prevCoordFormat = useRef(coordFormat);

	useEffect(() => {
		if (prevCoordFormat.current === coordFormat) return;

		setValue('lat', '');
		setValue('lng', '');
		clearErrors(['lat', 'lng']);

		prevCoordFormat.current = coordFormat;
	}, [clearErrors, coordFormat, setValue]);

	const onClickBackHandler = () => {
		saveValues(getValues());
		setCurrentTab(tabsNames.basicInformation);
	};

	const isLatLonCoordFormat = watch('coordFormat') === ECoordFormat.latlon;

	const coordFormatLeftLabel = isLatLonCoordFormat ? 'Latitude' : 'Easting';
	const coordFormatRightLabel = isLatLonCoordFormat ? 'Longitude' : 'Northing';

	const leftPlaceholder = isLatLonCoordFormat ? 'Enter latitude' : 'Enter easting';
	const rightPlaceholder = isLatLonCoordFormat ? 'Enter longitude' : 'Enter northing';

	const isRequiredAdPropertyNoName = getIsRequiredField({ fieldName: 'adPropertyNoName', requiredFields });
	const isRequiredAdStreetName = getIsRequiredField({ fieldName: 'adStreetName', requiredFields });
	const isRequiredAdLocality = getIsRequiredField({ fieldName: 'adLocality', requiredFields });
	const isRequiredAdTownCity = getIsRequiredField({ fieldName: 'adTownCity', requiredFields });
	const isRequiredAdPostCode = getIsRequiredField({ fieldName: 'adPostCode', requiredFields });
	const isRequiredLocationDescription = getIsRequiredField({ fieldName: 'locationDescription', requiredFields });

	return (
		<div ref={addressRef}>
			<GridContainer
				columns={2}
				customClassName={styles.addressFormContainer}
				gap='15px 20px'
				gridTemplateAreas={addressAreas}
			>
				<div className={styles.adPropertyNoName}>
					<Input
						errorMessage={errors?.adPropertyNoName?.message}
						label='Property No./Name'
						name='adPropertyNoName'
						placeholder='Enter property no./name'
						register={register}
						required={isRequiredAdPropertyNoName}
					/>
				</div>
				<div className={styles.adStreetName}>
					<Input
						errorMessage={errors?.adStreetName?.message}
						label='Street Name'
						name='adStreetName'
						placeholder='Enter street name'
						register={register}
						required={isRequiredAdStreetName}
					/>
				</div>
				<div className={styles.adLocality}>
					<Input
						errorMessage={errors?.adLocality?.message}
						label='Locality'
						name='adLocality'
						placeholder='Enter locality'
						register={register}
						required={isRequiredAdLocality}
					/>
				</div>
				<div className={styles.adTownCity}>
					<Input
						errorMessage={errors?.adTownCity?.message}
						label='Town/City'
						name='adTownCity'
						placeholder='Enter town/city'
						register={register}
						required={isRequiredAdTownCity}
					/>
				</div>
				<div className={styles.adPostCode}>
					<Input
						errorMessage={errors?.adPostCode?.message}
						label='Postcode'
						name='adPostCode'
						placeholder='Enter postcode'
						register={register}
						required={isRequiredAdPostCode}
					/>
				</div>
				<div className={styles.ngr}>
					<Input
						errorMessage={errors?.ngr?.message}
						label='NGR'
						name='ngr'
						placeholder='Enter NGR'
						register={register}
					/>
				</div>
				<div className={styles.coordFormat}>
					<Label label='Coordinates' />
					<div className={styles.radioButtonsContainer}>
						<RadioButton
							key={ECoordFormat.latlon}
							checked={watch('coordFormat') === ECoordFormat.latlon}
							id={ECoordFormat.latlon}
							label='Latitude and Longitude'
							name='coordFormat'
							register={register}
							setValue={setValue}
							value={ECoordFormat.latlon}
							withUseForms
						/>
						<RadioButton
							key={ECoordFormat.utm}
							checked={watch('coordFormat') === ECoordFormat.utm}
							id={ECoordFormat.utm}
							label='Easting and Northing'
							name='coordFormat'
							register={register}
							setValue={setValue}
							value={ECoordFormat.utm}
							withUseForms
						/>
					</div>
				</div>
				<div className={styles.lat}>
					<Input
						errorMessage={errors?.lat?.message}
						label={coordFormatLeftLabel}
						name='lat'
						placeholder={leftPlaceholder}
						regexValidation={regExpDecimalAndHyphen}
						register={register}
						required
						setValue={setValue}
					/>
				</div>
				<div className={styles.lng}>
					<Input
						errorMessage={errors?.lng?.message}
						label={coordFormatRightLabel}
						name='lng'
						placeholder={rightPlaceholder}
						regexValidation={regExpDecimalAndHyphen}
						register={register}
						required
						setValue={setValue}
					/>
				</div>
				<div className={styles.locationDescription}>
					<TextArea
						errorMessage={errors?.locationDescription?.message}
						label='Location Description'
						name='locationDescription'
						placeholder='Enter location description'
						register={register}
						required={isRequiredLocationDescription}
						rows={3}
						withUseForms
					/>
				</div>
				{!isEditMode
					&& <div className={styles.buttons}>
						<Button
							onClick={onClickBackHandler}
							text='Back'
							withoutBackground
						/>
						<Button
							onClick={onSubmit}
							text='Next'
						/>
					</div>
				}
			</GridContainer>
		</div>
	);
};

export default memo(Address);
